import {
  getParticipantLineItemGroups,
  type OrderingContext,
  prepareLedger,
} from '@order/features-ordering';
import {
  getSharedLineItemAttribution,
  getSharedLineItemCartLineItemTelemetry,
} from '@order/shared-line-item';
import { type TelemetryEvent, telemetryProductFromInput } from '@order/Telemetry';
import { convertPriceToFloat } from '@order/utils';

import {
  checkIfBundleCartLineItem,
  getBundleAttribution,
} from '../../../helpers/bundle-attribution';

// Used by `ga.view_cart` and `ga.begin_checkout`. Google only wants order value of line items
// without order credit, tax, etc
export const getBagTelemetryData = (
  context: OrderingContext,
): TelemetryEvent<'ga.begin_checkout'>['payload'] => {
  const data = getCheckoutTelemetryData(context);

  const orderValue = data.products?.reduce(
    (total, { cost = 0, quantity = 0 }) => total + cost * quantity,
    0,
  );

  return { ...data, orderValue };
};

/**
 * Converts the machine context in the telemetry event data for purchase events.
 */
export const getCheckoutTelemetryData = (
  context: OrderingContext,
  selectedPaymentMethodType?: string,
): TelemetryEvent<'checkout.start'>['payload'] => {
  const sharedLineItemAttribution = getSharedLineItemAttribution();
  const bundleAttribution = getBundleAttribution();

  const products = context.cart.lineItems?.map(
    ({ id: lineItemId, product, perItemCost, quantity, customName }) => {
      const sharedLineItemTelemetry = getSharedLineItemCartLineItemTelemetry({
        attribution: sharedLineItemAttribution,
        cartLineItemId: lineItemId,
      });

      const isBundleLineItem = checkIfBundleCartLineItem({
        attribution: bundleAttribution,
        cartLineItemId: lineItemId,
      });

      return telemetryProductFromInput({
        ...product,
        ...sharedLineItemTelemetry,
        isBundleLineItem,
        restaurantId: context.cart?.restaurantId,
        location: context.cart?.locationName,
        cost: perItemCost,
        quantity,
        name: customName ?? product?.name,
      });
    },
  );
  const hasSharedLineItem = products?.some(({ isSharedLineItem }) => Boolean(isSharedLineItem));
  const hasBundleLineItem = products?.some(({ isBundleLineItem }) => Boolean(isBundleLineItem));

  const bagCount = products
    ?.map(({ quantity }) => quantity)
    .reduce((a, b) => (a ?? 0) + (b ?? 0), 0);

  const isGroupOrder = context.cart.isGroupOrder;
  const groupOrderParticipantCount = isGroupOrder
    ? getParticipantLineItemGroups(context.cart.lineItems ?? []).length
    : undefined;

  const { total } = prepareLedger({
    ledger: context.cart?.ledger,
    tip: context.selectedTip,
    availableCredit: context.availableCredit,
    shouldUseCredit: context.shouldUseCredit,
    amountEligibleForFlex: context.amountEligibleForFlex,
    shouldUseFlex: context.shouldUseFlex,
  });

  return {
    selectedPaymentMethodType,
    orderId: context.cart?.orderId,
    restaurantId: context.cart?.restaurantId,
    restaurantName: context.cart?.locationName,
    orderType: context.cart?.orderChannel,
    products,
    bagCount,
    orderValue: convertPriceToFloat(total),
    street: context.cart?.deliveryOrderDetail?.address?.street,
    city: context.cart?.deliveryOrderDetail?.address?.city,
    state: context.cart?.deliveryOrderDetail?.address?.state,
    zipCode: context.cart?.deliveryOrderDetail?.address?.zipCode,
    country: context.cart?.deliveryOrderDetail?.address?.country,
    isNewCustomer: context.customer.isNewCustomer,
    customer: {
      firstName: context.customer.firstName,
      lastName: context.customer.lastName,
      email: context.customer.email,
      phoneNumber: context.customer.phoneNumber,
    },
    hasSharedLineItem: Boolean(hasSharedLineItem),
    hasBundleLineItem: Boolean(hasBundleLineItem),
    isGroupOrder,
    groupOrderParticipantCount,
  };
};
